/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, NgZone, OnDestroy, OnInit} from '@angular/core';
import * as _ from 'lodash';
import moment from 'moment';
import {AppEventBusService} from '../../common/events/app-event-bus.service';

import template from './notifications-tab.component.html';
import {objectTypeStateParams} from '../../common/filters/object-type-state-params';
import {AdnMessageParamsService} from '../../common/filters/adn-message-params.service';
import {AppShellStateService} from '../app-shell-state.service';
import {NotificationResource} from '../../../components/api/resources/resource/notification.resource';
import {UserProfileResource} from '../../../components/api/resources/resource/user-profile.resource';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';


const NOTIFICATION_SYNC_INTERVAL = 45000;

@Component({
  standalone: false,
  selector: 'adn-notifications-tab',
  template
})
export class NotificationsTabComponent implements OnInit, OnDestroy {
  notifications: any[] = [];

  private networkId: string;
  private localUser: any;
  private timer: any;
  private destroyLoginRequiredListener: () => void;
  private destroyLoginConfirmedListener: () => void;

  constructor(
    readonly shell: AppShellStateService,
    private readonly eventBus: AppEventBusService,
    @Inject(NotificationResource) private readonly Notification: any,
    @Inject(UserProfileResource) private readonly UserProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnMessageParams: AdnMessageParamsService,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    this.networkId = this.LocalNetworkProfile.getNetworkId();
    this.localUser = this.localUserProfile.get();
    this.markAllRead();

    this.startTimer();

    this.destroyLoginRequiredListener = this.eventBus.on('event:auth-loginRequired', () => {
      this.stopTimer();
    });
    this.destroyLoginConfirmedListener = this.eventBus.on('event:auth-loginConfirmed', () => {
      this.startTimer();
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
    if (this.destroyLoginRequiredListener) {
      this.destroyLoginRequiredListener();
    }
    if (this.destroyLoginConfirmedListener) {
      this.destroyLoginConfirmedListener();
    }
  }

  clear($event: Event): void {
    $event.stopPropagation();
    this.localUserProfile.safeSaveProfile(['adnNotificationsSince', this.networkId], moment());
    this.updateProfile();
    this.notifications = [];
    this.shell.unread = 0;
  }

  getColorLabel(notification: any, prefix: string): string {
    if (~notification.message.code.indexOf('fail')) {
      return prefix + 'warning';
    }
    if (~notification.message.code.indexOf('pass')) {
      return prefix + 'success';
    }
    if (~notification.message.code.indexOf('live')) {
      return prefix + 'success';
    }
    return prefix + notification.type.toLowerCase();
  }

  /**
   * Externally hosted destinations (report CDN URLs, etc.). Prefer plain href
   * over uiSref so the browser opens the real URL (usually target=_blank).
   */
  getNotificationExternalHref(notification: any): string | null {
    return notification.message?.parameters?.url
      || notification.object?.result
      || notification.object?.reportUrl
      || null;
  }

  isExternalNotification(notification: any): boolean {
    return !!(notification.objectType?.target || this.getNotificationExternalHref(notification));
  }

  getNotificationUiSref(notification: any): string | undefined {
    if (this.isExternalNotification(notification)) {
      return undefined;
    }
    const stateName = notification.objectType?.stateName;
    if (!stateName || stateName === '.') {
      return undefined;
    }
    return stateName;
  }

  getNotificationUiParams(notification: any): Record<string, any> {
    return objectTypeStateParams(notification.object, notification.objectType, notification.message?.parameters);
  }

  onExternalNotificationClick($event: Event, notification: any): void {
    if (!this.getNotificationExternalHref(notification)) {
      $event.preventDefault();
    }
  }

  private markAllRead(): void {
    this.shell.unread = 0;
    this.localUserProfile.safeSaveProfile(['adnNotificationsRead', this.networkId], moment());
    this.updateProfile();
    _.forEach(this.notifications, (notification) => {
      notification.read = true;
    });
  }

  private updateNotifications = (): void => {
    const profile = this.localUserProfile.getProfile();
    const timeSince = _.get(profile, ['adnNotificationsSince', this.networkId]);
    this.Notification.query(timeSince ? moment(timeSince) : undefined, (notifications: any[]) => {
      if (notifications.length !== this.notifications.length || (notifications.length > 0 && !notifications[0].equals(this.notifications[0]))) {
        this.ngZone.run(() => {
          this.shell.unread = 0;
          this.notifications = _.map(notifications, (notification) => {
            const timeRead = _.get(profile, ['adnNotificationsRead', this.networkId]);
            notification.read = timeRead ? !notification.time.isAfter(timeRead) : false;
            notification.message = this.adnMessageParams.transform(notification.message);
            if (!notification.read && this.shell) {
              this.shell.unread++;
            }
            return notification;
          });
        });
      }
    });
  };

  private stopTimer = (): void => {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  private startTimer = (): void => {
    this.stopTimer();
    this.timer = setInterval(this.updateNotifications, NOTIFICATION_SYNC_INTERVAL);
    this.updateNotifications();
  };

  private getValidDate(unverifiedDate: any): moment.Moment {
    let sinceDate = unverifiedDate;
    if (_.isString(sinceDate)) {
      sinceDate = moment(sinceDate);
    }
    if (!sinceDate || !moment(sinceDate).isValid()) {
      sinceDate = moment.utc().subtract(1, 'year');
    }
    return sinceDate;
  }

  private updateProfile(): void {
    const profile = this.localUserProfile.getProfile();
    this.UserProfile.save({
      id: this.localUser.userId,
      profile: _.pick(profile, ['adnNotificationsSince', 'adnNotificationsRead'])
    }, (apiUser: any) => {
      this.localUser = this.localUserProfile.safeSaveProfile(['adnNotificationsSince', this.networkId], this.getValidDate(_.get(apiUser, ['profile', 'adnNotificationsSince', this.networkId])));
      this.localUser = this.localUserProfile.safeSaveProfile(['adnNotificationsRead', this.networkId], this.getValidDate(_.get(apiUser, ['profile', 'adnNotificationsRead', this.networkId])));
    });
  }
}
